import 'range-slider-wc';
import {debounce} from '../utils/debounce';
import {filterSearchParams} from '../utils/search-params';

import initPagination from './pagination';

const selectors = {
  resultsNavbar: '[data-results-navbar]',
  resultsNavbarForm: '[data-results-navbar-form]',
  resultsGrid: '[data-results-grid]',
  productViewCheckbox: '[data-product-view-checkbox]',
  pagination: '[data-results-pagination]',
  activeFilters: '[data-results-active-filters]',
  filterContent: '[data-results-filter-content]',
  filterClearLink: '[data-results-filter-clear-link]',
  filterPriceRangeSlider: '[data-results-filter-price-range-slider]',
  filterPriceRangeMin: '[data-results-filter-price-range-min]',
  filterPriceRangeMax: '[data-results-filter-price-range-max]',
  filterPriceMinLabel: '[data-results-filter-label-price-min]',
  filterPriceMaxLabel: '[data-results-filter-label-price-max]',
  panelToggle: '[data-results-navbar-toggle]',
  panelsContainer: '[data-results-navbar-panels]',
  filtersPanel: '[data-results-navbar-panel="filters"]',
  sortPanel: '[data-results-navbar-panel="sort"]',
};

const classes = {
  hide: 'hide',
  navbarSticky: 'results-navbar--sticky',
  activeFiltersSticky: 'results-navbar__active-filters--sticky',
  formSubmitted: 'navbar-form-submitted',
  toggleActive: 'results-navbar__toggle--active',
  panelsActive: 'results-navbar__panels--active',
};

const ResultsFilterSort = (container, id) => {
  const defaultSortBy = container.dataset.defaultSortBy || 'manual';
  const resultsGrid = container.querySelector(selectors.resultsGrid);
  const resultsNavbar = container.querySelector(selectors.resultsNavbar);
  const resultsNavbarForm = container.querySelector(selectors.resultsNavbarForm);
  const selectedLayoutView = window.sessionStorage.getItem('resultsProductView');

  initNavbar();
  initPagination(id);

  function initNavbar() {
    if (!resultsNavbarForm || !resultsGrid) return;

    const activeFilters = container.querySelector(selectors.activeFilters);
    const filterContent = container.querySelectorAll(selectors.filterContent);
    const pagination = container.querySelectorAll(selectors.pagination);

    const renderPage = async (searchParams, currentFilter = false) => {
      const url = `${window.location.pathname}?section_id=${id}&${searchParams}`;
      try {
        const response = await fetch(url);
        if (response.ok === false) {
          throw new Error(`Fetch "${id}" section response was not OK`);
        }
        const html = await response.text();
        const newDocument = new DOMParser().parseFromString(html, 'text/html');

        resultsGrid.innerHTML = newDocument.querySelector(selectors.resultsGrid).innerHTML;

        document.dispatchEvent(new CustomEvent('results-grid:loaded'));

        document.body.classList.remove(classes.formSubmitted);

        // Replace each filter content separately
        if (filterContent) {
          filterContent.forEach((content, index) => {
            // Don't reload the current filter because it causes bugs
            if (currentFilter && content === currentFilter) {
              return;
            }
            const filterContentNew = newDocument.querySelectorAll(selectors.filterContent);
            content.innerHTML = filterContentNew[index].innerHTML;
          });
        }

        if (activeFilters) {
          activeFilters.innerHTML = newDocument.querySelector(selectors.activeFilters).innerHTML;
        }

        if (pagination) {
          pagination.forEach((item, index) => {
            const paginationNew = newDocument.querySelectorAll(selectors.pagination);
            if (!paginationNew[index]) return;
            item.innerHTML = paginationNew[index].innerHTML;
          });
          initPagination(id);
        }

        history.replaceState(
          {searchParams},
          '',
          `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
        );
        initPriceRangeSlider();
      } catch (err) {
        window.console.error(err);
      }
    };

    const handleNavbarFormSubmit = (event) => {
      event.preventDefault();
      document.body.classList.add(classes.formSubmitted);
      const formData = new FormData(event.target.closest('form'));
      const searchParams = new URLSearchParams(formData);
      const keyValuePairsToRemove = [{key: 'sort_by', value: defaultSortBy}];
      const filterPriceRangeMin = container.querySelector(selectors.filterPriceRangeMin);
      if (filterPriceRangeMin) {
        keyValuePairsToRemove.push({key: filterPriceRangeMin.name, value: filterPriceRangeMin.min});
      }
      const filterPriceRangeMax = container.querySelector(selectors.filterPriceRangeMax);
      if (filterPriceRangeMax) {
        keyValuePairsToRemove.push({key: filterPriceRangeMax.name, value: filterPriceRangeMax.max});
      }
      const filteredSearchParams = filterSearchParams(searchParams, keyValuePairsToRemove);
      const currentFilter = event.target.closest(selectors.filterContent);

      renderPage(filteredSearchParams, currentFilter);
    };

    const debouncedNavbarFormSubmit = debounce((event) => {
      handleNavbarFormSubmit(event);
    }, 500);

    resultsNavbarForm.addEventListener('input', debouncedNavbarFormSubmit);
    resultsNavbarForm.addEventListener('submit', handleNavbarFormSubmit);

    // Toggle filters and sort panels
    const panelToggles = resultsNavbarForm.querySelectorAll(selectors.panelToggle);
    const panelsContainer = resultsNavbarForm.querySelector(selectors.panelsContainer);
    const filtersPanel = panelsContainer?.querySelector(selectors.filtersPanel);
    const sortPanel = panelsContainer?.querySelector(selectors.sortPanel);

    let panelsOpenedFirstTime = true;

    let panelsContainerActive = false;

    const expandPanel = (panel) => {
      if (!panelsContainer || !panel) return;

      panel.classList.remove(classes.hide);

      panelsContainer.style.height = `${panel.offsetHeight}px`;
      panelsContainer.classList.add(classes.panelsActive);

      panelsContainerActive = true;
    };

    const collapsePanel = (panel) => {
      if (!panelsContainer || !panel) return;

      panelsContainer.classList.remove(classes.panelsActive);
      panelsContainer.removeAttribute('style');
      panelsContainer.addEventListener(
        'transitionend',
        () => {
          panel.classList.add(classes.hide);
        },
        {once: true}
      );

      panelsContainerActive = false;
    };

    panelToggles?.forEach((panelToggle) => {
      panelToggle.addEventListener('click', () => {
        const activePanelToggle = resultsNavbarForm.querySelector(`.${classes.toggleActive}`);

        if (activePanelToggle && activePanelToggle !== panelToggle) {
          activePanelToggle.classList.remove(classes.toggleActive);
        }

        panelToggle.classList.toggle(classes.toggleActive);

        // Set initial height (so we can animate it further down and have a smooth transition when panels changed)
        panelsContainer.style.height = `${panelsContainer.offsetHeight}px`;

        const selectedPanelName = panelToggle.dataset.resultsNavbarToggle;
        const selectedPanel = selectedPanelName === 'filters' ? filtersPanel : sortPanel;
        const otherPanel = selectedPanelName === 'filters' ? sortPanel : filtersPanel;

        if (!selectedPanel) return;

        // Hide other panel
        otherPanel?.classList.add(classes.hide);

        // Check state for selected panel
        const isCollapsed = selectedPanel.classList.contains(classes.hide);

        // Expand selected panel
        if (isCollapsed) {
          expandPanel(selectedPanel);

          // Open first accordion the first time the filters are opened
          if (panelsOpenedFirstTime && selectedPanelName === 'filters') {
            const firstAccordion = selectedPanel.querySelector('.accordion__panel');
            const firstAccordionTrigger = selectedPanel.querySelector('.accordion__trigger');

            if (firstAccordion && firstAccordion.clientHeight === 0) {
              firstAccordion.style.transition = 'none';
              firstAccordion.style.height = `${firstAccordion.scrollHeight}px`;
              firstAccordionTrigger?.setAttribute('aria-expanded', true);
              panelsContainer.style.height = `${selectedPanel.offsetHeight}px`;
              firstAccordion.style.transition = '';
            }

            panelsOpenedFirstTime = false;
          }

          return;
        }

        // Collapse selected panel
        collapsePanel(selectedPanel);
      });
    });

    // Remove style attribute from panelsContainer when an accordion is toggled
    // just so the panelsContainer adjusts it height to it's content
    const accordionTriggers = panelsContainer.querySelectorAll('.accordion__trigger');
    accordionTriggers?.forEach((accordionTrigger) => {
      accordionTrigger.addEventListener('click', () => {
        panelsContainer.removeAttribute('style');
      });
    });

    // Collapse active panel when you click outside of navbar box
    document.addEventListener('click', (event) => {
      if (!panelsContainerActive) return;

      const isNavbarBox = Boolean(event.target.closest('.results-navbar__box'));
      if (isNavbarBox) return;

      const activePanelToggle = resultsNavbarForm.querySelector(`.${classes.toggleActive}`);
      activePanelToggle?.classList.remove(classes.toggleActive);

      const selectedPanelName = activePanelToggle?.dataset.resultsNavbarToggle;
      const selectedPanel = selectedPanelName === 'filters' ? filtersPanel : sortPanel;

      collapsePanel(selectedPanel);
    });

    // Filter clear links
    if (activeFilters) {
      const handleClearFiltersLinkClick = (event) => {
        const filterClearLink = event.target.closest(selectors.filterClearLink);
        if (!filterClearLink) return;

        event.preventDefault();
        const searchParams = filterClearLink.href.split('?')[1] ?? '';
        renderPage(searchParams);
      };
      activeFilters.addEventListener('click', handleClearFiltersLinkClick);
    }

    if (filterContent) {
      // Price range filter
      initPriceRangeSlider();
    }

    // Product view switcher
    const productViewCheckbox = container.querySelector(selectors.productViewCheckbox);
    if (productViewCheckbox) {
      const handleProductViewSwitch = (view) => {
        const classToAdd = `results-grid--${view}`;
        const classesToRemove = Array.from(resultsGrid.classList).filter(
          (value) => value.includes('results-grid--') && value !== classToAdd
        );

        resultsGrid.classList.add(classToAdd);
        resultsGrid.classList.remove(...classesToRemove);

        window.sessionStorage.setItem('resultsProductView', view);

        productViewCheckbox.checked = view === 'model';
      };

      if (selectedLayoutView) {
        handleProductViewSwitch(selectedLayoutView);
      }

      productViewCheckbox.addEventListener('change', () => {
        const view = productViewCheckbox.checked ? 'model' : 'product';
        handleProductViewSwitch(view);
      });
    }

    // Observer to change position for results navbar from fixed to absolute when the footer is reached
    const footerSection = document.querySelector('.footer-section');
    if (resultsNavbar && footerSection) {
      const desktopQuery = window.matchMedia(`(min-width: ${window.theme.breakpoints.md}px)`);

      const calculateRootMargin = () => {
        const announcementBar = document.querySelector('[data-section-type="announcement"]');

        return announcementBar
          ? `-${announcementBar.offsetHeight}px 0px -${announcementBar.offsetHeight}px 0px`
          : '0px 0px 0px 0px';
      };

      let rootMargin = calculateRootMargin();

      let observer = null;

      const initFooterObserver = () => {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              resultsNavbar.classList.toggle(classes.navbarSticky, !entry.isIntersecting);

              if (desktopQuery.matches) return;
              activeFilters.classList.toggle(classes.activeFiltersSticky, !entry.isIntersecting);
            });
          },
          {rootMargin}
        );
        observer.observe(footerSection);
      };

      initFooterObserver();

      document.addEventListener('announcement-bar-hide', () => {
        if (!observer) return;

        observer.disconnect();
        rootMargin = calculateRootMargin();
        initFooterObserver();
      });
    }
  }

  function initPriceRangeSlider() {
    const filterPriceRangeSlider = container.querySelector(selectors.filterPriceRangeSlider);
    const filterPriceRangeMin = container.querySelector(selectors.filterPriceRangeMin);
    const filterPriceRangeMax = container.querySelector(selectors.filterPriceRangeMax);
    const filterPriceMinLabel = container.querySelector(selectors.filterPriceMinLabel);
    const filterPriceMaxLabel = container.querySelector(selectors.filterPriceMaxLabel);
    if (!filterPriceRangeSlider || !filterPriceRangeMin || !filterPriceRangeMax) return;

    filterPriceRangeSlider.addEventListener('input', (event) => {
      const {valueMin, valueMax} = event.detail;
      filterPriceRangeMin.value = valueMin;
      filterPriceRangeMax.value = valueMax;

      if (filterPriceMinLabel) {
        filterPriceMinLabel.textContent = valueMin;
      }
      if (filterPriceMaxLabel) {
        filterPriceMaxLabel.textContent = valueMax;
      }
    });

    filterPriceRangeSlider.addEventListener('change', () => {
      if (resultsNavbarForm.requestSubmit) {
        return resultsNavbarForm.requestSubmit();
      }

      const submitButton = resultsNavbarForm.querySelector('[type="submit"]');
      if (!submitButton) return;

      submitButton.click();
    });
  }
};

export default ResultsFilterSort;
